import React from 'react';
import { Statistic } from '../components/StockTable';

// Historical price statistics for daily view
export const HISTORICAL_PRICE_STATISTICS: Statistic[] = [
  { key: 'date', label: 'Date' },
  { key: 'close_price_adjusted', label: 'Closing Price' },
  { key: 'num_transactions', label: 'Transactions' },
  { key: 'volume_adjusted', label: 'Volume' },
];

// Timeframe options for dropdown
export const TIMEFRAME_OPTIONS = [
  { value: '1year', label: '1 Year' },
  { value: '2years', label: '2 Years' },
  { value: '3years', label: '3 Years' },
  { value: '4years', label: '4 Years' },
  { value: '5years', label: '5 Years' },
] as const;

export type Timeframe = typeof TIMEFRAME_OPTIONS[number]['value'];

// Formatting functions for daily price data
export const formatDailyPrice = (price: number): string => {
  return `$${price.toFixed(2)}`;
};

export const formatDailyNumber = (num: number): string => {
  return num.toLocaleString();
};

/** Volume: always show as a whole number (split-adjusted floats may have decimals). */
export const formatDailyVolume = (volume: number): string => {
  return Math.round(volume).toLocaleString();
};

export const formatDailyDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

// Format statistic value for daily price data
export const formatDailyPriceStatisticValue = (
  statisticKey: string,
  data: { date: string; close_price_adjusted: number; num_transactions: number; volume_adjusted: number },
  isLoading?: boolean
): React.ReactNode => {
  if (isLoading) {
    return <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded"></div>;
  }
  
  switch (statisticKey) {
    case 'date':
      return formatDailyDate(data.date);
    case 'close_price_adjusted':
      return formatDailyPrice(data.close_price_adjusted);
    case 'num_transactions':
      return formatDailyNumber(data.num_transactions);
    case 'volume_adjusted':
      return formatDailyVolume(data.volume_adjusted);
    default:
      return '--';
  }
};

// Get statistic color for daily price data
export const getDailyPriceStatisticColor = (
  statisticKey: string,
  data: { date: string; close_price_adjusted: number; num_transactions: number; volume_adjusted: number },
  index: number
): string => {
  // For price data, we could add color logic based on price changes
  // For now, return neutral colors
  return 'text-text-light-neutral dark:text-text-secondary';
};

